import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEditingAsset,
  selectEditingVariantId,
  setEditingVariantId,
} from "../../app/slices/AssetsSlice";
import {
  BrowseDialogViewMode,
  selectBrowseDialogViewMode,
  setBrowseDialogViewMode,
} from "../../app/slices/GlobalSlice";
import {
  LanguageData,
  selectAvailableLanguages,
} from "../../app/slices/TrainingLocalizationSlice";
import PiBrowseLayout from "../../components/PiBrowseLayout";
import {
  browseListboxId,
  browseOptionId,
} from "../../components/browseListboxIds";
import { navigateAssetGrid } from "../file_explorer/navigateAssetGrid";
import { GridDirection } from "../interaction_picker/navigateGroupedGrid";
import { AssetKey, AssetVariantDTO } from "../../dto/AssetDTO";
import { useAssetDetails } from "../../hooks/GetAssetDetailsHook";
import { useGetLanguages } from "../../hooks/GetLanguagesHook";
import { useSaveVariant } from "../../hooks/SaveVariantHook";
import useFilterableList from "../../hooks/useFilterableList";
import VariantDetailsPane from "./VariantDetailsPane";
import VariantResults, { DEFAULT_VARIANT_COLUMNS } from "./VariantResults";
import { languageCodesEqual } from "./languageCode";
import {
  LOADING_VARIANT_ID,
  orderVariants,
  variantFileName,
  variantKind,
  variantLanguage,
} from "./variantDisplay";

interface LocalizeFileBodyProps {
  onClose: () => void;
}

const DIALOG = "localize";

/**
 * Everything inside the localize dialog. One localization is edited at a time:
 * the list picks it, the details pane stages a language and/or a replacement
 * file, and the footer commits. Uploading is deliberately decoupled from
 * assigning — a file can land here with no language and be given one later,
 * because localizing into a given language is optional.
 */
export default function LocalizeFileBody(props: LocalizeFileBodyProps) {
  const { onClose } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const asset = useSelector(selectEditingAsset);
  const editingVariantId = useSelector(selectEditingVariantId);
  const languages = useSelector(selectAvailableLanguages);

  const [getAssetDetails] = useAssetDetails();
  const [getLanguages] = useGetLanguages();
  const [saveVariant] = useSaveVariant();

  const [filter, setFilter] = useState("");
  const [pendingLanguage, setPendingLanguage] = useState<LanguageData>();
  const [pendingFile, setPendingFile] = useState<File>();
  const [pendingFilePreview, setPendingFilePreview] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [tileColumns, setTileColumns] = useState(DEFAULT_VARIANT_COLUMNS);

  // Shared with the other browse dialogs: one "how I like to browse"
  // preference rather than four.
  const viewMode = useSelector(selectBrowseDialogViewMode);

  const assetId = asset?.id;
  const assetKey = asset?.key ?? AssetKey.Unsupported;
  const variants = useMemo(() => asset?.variants ?? [], [asset]);

  useEffect(() => {
    if (!assetId) return;
    getAssetDetails(assetId);
    getLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  const ordered = useMemo(
    () => orderVariants(variants, languages),
    [variants, languages]
  );

  const visible = useMemo(() => {
    if (!filter) return ordered;
    const query = filter.toLowerCase();
    return ordered.filter((variant) =>
      searchStringsFor(variant, languages, t).some((value) =>
        value?.toLowerCase().includes(query)
      )
    );
  }, [ordered, filter, languages, t]);

  const selectedIndex = Math.max(
    0,
    visible.findIndex((v) => v.id === editingVariantId)
  );

  // Staged edits belong to one variant; moving off it drops them.
  const resetPending = useCallback(() => {
    setPendingLanguage(undefined);
    setPendingFile(undefined);
    setPendingFilePreview(undefined);
  }, []);

  const selectVariant = useCallback(
    (variant: AssetVariantDTO | undefined) => {
      if (!variant || variant.id === LOADING_VARIANT_ID) return;
      if (variant.id === editingVariantId) return;
      resetPending();
      dispatch(setEditingVariantId(variant.id));
    },
    [dispatch, editingVariantId, resetPending]
  );

  // Nothing selected yet — land on the first row so the pane is never blank.
  useEffect(() => {
    if (editingVariantId || ordered.length === 0) return;
    dispatch(setEditingVariantId(ordered[0].id));
  }, [dispatch, editingVariantId, ordered]);

  // A finished upload is the one moment the dialog moves the selection on its
  // own: the whole point of "you pick the language after" is that the new file
  // is waiting in the pane with the dropdown open for business.
  const knownIds = useRef<Set<string> | undefined>(undefined);
  useEffect(() => {
    const ids = variants
      .map((v) => v.id)
      .filter((id) => id !== LOADING_VARIANT_ID);

    if (!knownIds.current) {
      knownIds.current = new Set(ids);
      return;
    }

    const fresh = ids.find((id) => !knownIds.current!.has(id));
    ids.forEach((id) => knownIds.current!.add(id));
    if (fresh) {
      resetPending();
      dispatch(setEditingVariantId(fresh));
    }
  }, [variants, dispatch, resetPending]);

  const selectedVariant = variants.find((v) => v.id === editingVariantId);
  const isDirty = !!pendingLanguage || !!pendingFile;
  const showDropzone = !filter;

  /**
   * Every language not already spoken for, plus whichever one this variant
   * holds — otherwise the current value would be missing from its own list.
   */
  const languageOptions = useMemo(() => {
    if (!selectedVariant) return [];
    const taken = variants
      .filter((v) => v.id !== selectedVariant.id)
      .map((v) => v.variant);
    return languages.filter(
      (lang) => !taken.some((code) => languageCodesEqual(code, lang.shortcode))
    );
  }, [languages, variants, selectedVariant]);

  const onSave = useCallback(async () => {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    try {
      await saveVariant(pendingLanguage?.shortcode, pendingFile);
      resetPending();
    } finally {
      setIsSaving(false);
    }
  }, [isDirty, isSaving, saveVariant, pendingLanguage, pendingFile, resetPending]);

  // The drop zone occupies cell 0 of the grid but is never navigable, so the
  // arrow geometry is the file explorer's — same offset problem, same answer.
  const moveHighlight = useCallback(
    (index: number, direction: GridDirection) =>
      navigateAssetGrid(
        visible.length,
        viewMode === "tile" ? tileColumns : 1,
        showDropzone ? 1 : 0,
        index,
        direction
      ),
    [visible.length, viewMode, tileColumns, showDropzone]
  );

  const list = useFilterableList<AssetVariantDTO>({
    items: visible,
    getSearchStrings: (variant) => searchStringsFor(variant, languages, t),
    filter,
    onFilterChange: setFilter,
    moveHighlight,
    initiallyOpen: true,
    focusSearchOnSlash: true,
    onSelect: () => {
      // Enter commits, matching the ↵ on the footer button. It never closes on
      // its own — the save is the outcome, and the list stays put so the next
      // localization is one arrow away.
      void onSave();
      return false;
    },
    onClose,
  });

  const { setSelectedIndex } = list;

  // The hook owns the highlight; the slice owns which variant is being edited.
  // Keep them in step in both directions.
  useEffect(() => {
    setSelectedIndex(selectedIndex);
  }, [setSelectedIndex, selectedIndex]);

  useEffect(() => {
    selectVariant(visible[list.selectedIndex]);
  }, [list.selectedIndex, visible, selectVariant]);

  const onFileSelected = useCallback((file: File) => {
    setPendingFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPendingFilePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const onSearchChange = useCallback((value: string) => setFilter(value), []);

  const onViewModeChange = useCallback(
    (mode: BrowseDialogViewMode) => {
      dispatch(setBrowseDialogViewMode(mode));
    },
    [dispatch]
  );

  return (
    <PiBrowseLayout
      searchValue={filter}
      onSearchChange={onSearchChange}
      searchPlaceholder={t("localize_ui.search_placeholder")}
      searchInputRef={list.searchInputRef}
      searchFieldId="localize-search"
      dataTestId="localize-file-dialog"
      searchTestId="localize-search-input"
      listboxId={browseListboxId(DIALOG)}
      activeDescendantId={browseOptionId(DIALOG, list.selectedIndex)}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      results={
        <VariantResults
          assetId={assetId ?? ""}
          assetKey={assetKey}
          variants={visible}
          viewMode={viewMode}
          selectedIndex={list.selectedIndex}
          onSelectIndex={(index) => setSelectedIndex(index)}
          showDropzone={showDropzone}
          listboxId={browseListboxId(DIALOG)}
          optionIdFor={(index) => browseOptionId(DIALOG, index)}
          onColumnsChange={setTileColumns}
        />
      }
      details={
        <VariantDetailsPane
          variant={selectedVariant}
          assetKey={assetKey}
          languageOptions={languageOptions}
          pendingLanguage={pendingLanguage}
          onLanguageChange={setPendingLanguage}
          pendingFilePreview={pendingFilePreview}
          onFileSelected={onFileSelected}
          isSaving={isSaving}
        />
      }
      primaryLabel={t("localize_ui.save_changes") ?? ""}
      primaryDisabled={!isDirty || isSaving}
      onPrimaryAction={onSave}
    />
  );
}

/** Language name, code, and the labels the special rows show. */
function searchStringsFor(
  variant: AssetVariantDTO,
  languages: LanguageData[],
  t: (key: string) => string
) {
  const kind = variantKind(variant, languages);
  if (kind === "default") return [t("default")];
  if (kind === "loading") return [t("localize_ui.uploading")];
  if (kind === "unassigned")
    return [t("localize_ui.not_set"), variantFileName(variant)];

  const language = variantLanguage(variant, languages);
  return [language?.full, language?.shortcode, variantFileName(variant)];
}
